import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import cornerstone from "cornerstone-core";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import dicomParser from "dicom-parser";
import { baseUrl, userLocation, ipAddress, userName } from "../Univ.js";

// Setup Cornerstone loaders
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
cornerstoneWADOImageLoader.configure({
  beforeSend: (xhr) => {
    xhr.setRequestHeader("Accept", "application/dicom");
  },
});

function InvResultEntry() {
  const { inv_id, itest_id } = useParams();
  const [invData, setInvData] = useState([]);
  const [resData, setResData] = useState([]);
  const [docs, setDocs] = useState([]); // DICOM URLs
  const [remarks, setRemarks] = useState("");
  const [temp, setTemp] = useState([]);
  const [appt, setAppt] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const viewerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false); // cine play/pause

  // Load investigation result
  useEffect(() => {
    axios
      .get(baseUrl + "inv-files-view/" + inv_id + "/" + itest_id + "/")
      .then((response) => {
        setResData(response.data);
        setRemarks(response.data.remarks || "");
      })
      .catch((error) => console.log("API Error:", error));
  }, [inv_id, itest_id]);

  // Load patient & test info
  useEffect(() => {
    axios
      .get(baseUrl + "inv-test/" + inv_id + "/")
      .then((response) => {
        const index = response.data[0].itest_id.split(",").indexOf(itest_id);
        const pat_id = response.data[0].pat.id;
        const patient = response.data[0].pat.patient;
        const cons = response.data[0].cons.consultant;
        const apptId = response.data[0].inv_id;
        if (index !== -1) {
          const inv_test = response.data[0].inv_test.split(",")[index];
          setInvData([{ itest_id, inv_test, pat_id, patient, cons, appt: apptId }]);
          setAppt(apptId);
        }
      })
      .catch((error) => console.log("Error:", error));
  }, [inv_id, itest_id]);

  // Load templates
  useEffect(() => {
    axios
      .get(`${baseUrl}template-create/`)
      .then((response) => setTemp(response.data))
      .catch((error) => console.log(error));
  }, []);

  // Handle template select
  const handleTemplateChange = (event) => {
    const selectedTemplateId = event.target.value;
    setSelectedTemplate(selectedTemplateId);
    axios
      .get(baseUrl + "edit-template/" + selectedTemplateId + "/")
      .then((response) => setRemarks(response.data.template || ""))
      .catch((error) => console.log(error));
  };

  // Fetch all DICOM files starting with itest_id
  useEffect(() => {
    if (!appt) return;
    axios
      .get(baseUrl + "get-dicom-files/" + appt + "/")
      .then((response) => {
        const files = response.data.files.map((f) => ({ uri: f }));
        setDocs(files);
        setCurrentIndex(0);
      })
      .catch((err) => console.log(err));
  }, [appt]);

  // Load a DICOM image
  const loadImage = (index) => {
    if (!docs.length) return;
    const element = viewerRef.current;
    if (!element) return;
    cornerstone.enable(element);
    const imageId = `wadouri:${docs[index].uri}`;
    cornerstone
      .loadAndCacheImage(imageId)
      .then((image) => cornerstone.displayImage(element, image))
      .catch((err) => console.error("Failed to load DICOM:", err));
  };

  // Handle cine loop
  useEffect(() => {
    loadImage(currentIndex);
    let cine;
    if (isPlaying && docs.length > 1) {
      cine = setInterval(() => setCurrentIndex((i) => (i + 1) % docs.length), 1000);
    }
    return () => {
      if (cine) clearInterval(cine);
    };
  }, [currentIndex, docs, isPlaying]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") setCurrentIndex((i) => (i + 1) % docs.length);
      if (e.key === "ArrowLeft") setCurrentIndex((i) => (i - 1 + docs.length) % docs.length);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [docs]);

  // Delete DICOM file
  const handleDelete = (fileUri) => {
    const fileName = fileUri.split("/").pop();
    Swal.fire({
      title: "Confirm",
      text: `Delete ${fileName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
            .delete(baseUrl + "delete-dicom-file/" + encodeURIComponent(fileName) + "/")
          .then(() => {
            Swal.fire("Deleted!", "File has been deleted.", "success");
            const updatedDocs = docs.filter((d) => d.uri !== fileUri);
            setDocs(updatedDocs);
            setCurrentIndex(updatedDocs.length ? 0 : -1);
          })
          .catch(() => Swal.fire("Error", "File could not be deleted", "error"));
      }
    });
  };

  // Submit form
  const formSubmit = (e) => {
    e.preventDefault();
    const _formData = new FormData();
    _formData.append("inv_id", inv_id);
    _formData.append("pat", invData[0].pat_id);
    _formData.append("itest_id", itest_id);
    _formData.append("remarks", remarks);
    _formData.append("status", "1");
    _formData.append("addedby", userName);
    _formData.append("ip_add", ipAddress);
    _formData.append("location", userLocation);
    _formData.append("invt_files", '');
//    if (docs.length > 0 && currentIndex >= 0) {
//      _formData.append("invt_files", docs[currentIndex].uri.split("/").pop());
//    }
    axios.post(`${baseUrl}invt-files-create/`, _formData)
        .then(response => {
            Swal.fire({
              title: 'Files Uploaded.',
              icon: 'success',
              timer: 5000,
              showConfirmButton: false
            });
            setTimeout(() => {
              window.location.href = '/inv-view/';
            }, 3000);
        })
            .catch(error => {
              console.log(error);
              Swal.fire(
                'Error!',
                'Failed. Please try again later.',
                'error'
              );
            });
  };

  return (
    <section>
      <form onSubmit={formSubmit}>
        {invData.map((row, index) => (
          <div key={index} style={{ marginBottom: "20px" }}>
            <table
              style={{
                border: "2px solid #ccc",
                width: "100%",
                margin: "20px 0",
                borderRadius: "5px",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#F2F2F2", borderBottom: "2px solid #ccc" }}>
                  <th style={{ padding: "10px", textAlign: "left" }}>PID: {row.pat_id}</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Patient: {row.patient}</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Consultant: Dr. {row.cons}</th>
                </tr>
              </thead>
            </table>

            <div style={{ display: "flex", gap: "20px" }}>
              {/* DICOM Viewer */}
              <div style={{ position: "relative" }}>
                <div ref={viewerRef} style={{ width: "512px", height: "512px", backgroundColor: "black" }} />
                {docs.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleDelete(docs[currentIndex].uri)}
                      style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        zIndex: 10,
                        background: "red",
                        color: "white",
                        border: "none",
                        padding: "5px 10px",
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                    {/* Navigation */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: "10px",
                        zIndex: 10,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setCurrentIndex((i) => (i - 1 + docs.length) % docs.length)}
                      >
                        ◀
                      </button>
                      <button type="button" onClick={() => setIsPlaying(!isPlaying)}>
                        {isPlaying ? "Pause" : "Play"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentIndex((i) => (i + 1) % docs.length)}
                      >
                        ▶
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Template + Quill */}
              <div style={{ flex: 1 }}>
                <div className="mb-3">
                  <label className="form-label">Select Template</label>
                  <select
                    name="selectedTemplate"
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    className="form-control"
                  >
                    <option>Select Template</option>
                    {temp.map((template, idx) => (
                      <option key={idx} value={template.id}>
                        {template.type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Report</label>
                  <ReactQuill style={{ height: "300px" }} value={remarks} onChange={setRemarks} />
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary mt-3">
              Submit
            </button>
          </div>
        ))}
      </form>
    </section>
  );
}

export default InvResultEntry;
